import { dbPut, normalizeCard } from '../core/db.js';
import { state } from '../core/state.js';
import { $, $$, clozeAnswers, esc, uid } from '../core/text.js';
import { modal } from '../ui/modal.js';
import { toast } from '../ui/toast.js';
import { refresh } from '../ui/views.js';

/* ══════════ Karten-Editor ══════════ */
export function cardModal(card, onSaved) {
  if (!card && !state.selCourse) { toast('Erst einen Kurs auswählen'); return; }
  const courseId = card?.courseId || state.selCourse;
  const decks = state.decks.filter((d) => d.courseId === courseId);
  if (!decks.length) { toast('Erst eine Vorlesung anlegen'); return; }
  const pre = card?.deckId || [...state.selDecks][0] || decks[0].id;
  const deckColor = decks.find((d) => d.id === pre)?.color ?? 0;
  const initGaps = card?.cloze ? clozeAnswers(card.front.text).split(',').map((s) => s.trim()).filter(Boolean) : [];
  const plainFront = card?.cloze ? unwrapCloze(card.front.text) : (card?.front.text || '');
  const draft = {
    front: { text: plainFront, image: card?.front.image || null },
    back: { text: card?.back.text || '', image: card?.back.image || null },
    color: card ? card.color : deckColor,
    cloze: !!card?.cloze,
  };
  let colorTouched = !!card;

  modal({
    title: card ? 'Karte bearbeiten' : 'Neue Karte',
    body: `
      <div class="field"><label>Vorlesung</label>
        <select class="select" id="m-deck">
          ${decks.map((d) => `<option value="${d.id}" ${d.id === pre ? 'selected' : ''}>${esc(d.name)}</option>`).join('')}
        </select></div>
      <label class="toggle cloze-toggle"><input type="checkbox" id="m-cloze" ${draft.cloze ? 'checked' : ''}>
        Lückentext – einzelne Wörter im Text zu Lücken machen</label>
      <div class="fmt-hint">Formatierung: <code>**fett**</code> · <code>*kursiv*</code> · Formel <code>$E=mc^2$</code> · Code <code>\`x=1\`</code></div>
      <div class="field"><label id="m-front-label">${draft.cloze ? 'Text' : 'Vorderseite – Frage'}</label>
        <textarea class="textarea" id="m-front" rows="3">${esc(draft.front.text)}</textarea>
        <div class="img-slot"><img id="m-front-img" style="${draft.front.image ? '' : 'display:none'}" src="${draft.front.image || ''}">
          <button class="file-btn" data-pick="front">Bild wählen</button>
          <button class="file-btn" data-clear="front">Entfernen</button></div></div>
      <div class="field cloze-box" id="m-cloze-box" style="${draft.cloze ? '' : 'display:none'}">
        <label>Lücken <small>(je Feld ein Wort/Ausdruck, der im Text ausgeblendet wird)</small></label>
        <div id="m-gaps"></div>
        <button type="button" class="btn-ghost outline tiny" id="m-add-gap">＋ Lücke hinzufügen</button>
      </div>
      <div class="field" id="m-back-field" style="${draft.cloze ? 'display:none' : ''}"><label>Rückseite – Antwort</label>
        <textarea class="textarea" id="m-back" rows="3">${esc(draft.back.text)}</textarea>
        <div class="img-slot"><img id="m-back-img" style="${draft.back.image ? '' : 'display:none'}" src="${draft.back.image || ''}">
          <button class="file-btn" data-pick="back">Bild wählen</button>
          <button class="file-btn" data-clear="back">Entfernen</button></div></div>
      <div class="field"><label>Schlagwörter (Tags, Komma-getrennt)</label>
        <input class="input" id="m-tags" placeholder="z. B. Anatomie, Klausur, wichtig" value="${esc((card?.tags || []).join(', '))}"></div>
      <div class="field"><label>Hinweis (optional, im Lernen mit H)</label>
        <input class="input" id="m-hint" placeholder="Eselsbrücke, Stichwort…" value="${esc(card?.hint || '')}"></div>
      <div class="field"><label>Farbe</label>
        <div class="swatches" id="m-swatches">
          ${Array.from({ length: 12 }, (_, i) => `<button class="swatch pc-${i} ${i === draft.color ? 'sel' : ''}" data-color="${i}"></button>`).join('')}
        </div></div>`,
    onOk: async () => {
      const cloze = $('#m-cloze').checked;
      let frontText = $('#m-front').value.trim();
      if (cloze) {
        const gaps = $$('#m-gaps .gap-inp').map((i) => i.value.trim()).filter(Boolean);
        if (!gaps.length) { toast('Füge mit „＋ Lücke" mindestens ein Wort hinzu'); return false; }
        const { text, missing } = wrapClozes(frontText, gaps);
        if (missing.length) { toast('Kommt so nicht im Text vor: ' + missing.join(', ')); return false; }
        frontText = text;
      }
      const front = { text: frontText, image: draft.front.image };
      const back = { text: cloze ? '' : $('#m-back').value.trim(), image: cloze ? null : draft.back.image };
      const tags = $('#m-tags').value.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 12);
      if (!front.text && !front.image) { toast('Vorderseite braucht Text oder Bild'); return false; }
      if (!cloze && !back.text && !back.image) { toast('Rückseite braucht Text oder Bild'); return false; }
      const rec = card
        ? { ...card, deckId: $('#m-deck').value, front, back, color: draft.color, cloze, tags, hint: $('#m-hint').value.trim() }
        : normalizeCard({ id: uid(), courseId, deckId: $('#m-deck').value, front, back, color: draft.color, cloze, tags, hint: $('#m-hint').value.trim() });
      await dbPut('cards', rec);
      if (card) state.cards = state.cards.map((c) => (c.id === rec.id ? rec : c));
      else state.cards.push(rec);
      toast(card ? 'Gespeichert ✦' : 'Karte erstellt ✦');
      if (onSaved) onSaved(rec);
      refresh();
    },
  });

  const setSwatch = () => { $$('#m-swatches .swatch').forEach((x) => x.classList.toggle('sel', +x.dataset.color === draft.color)); };
  $('#m-deck').onchange = (e) => { if (!colorTouched) { draft.color = state.decks.find((d) => d.id === e.target.value)?.color ?? 0; setSwatch(); } };
  $$('[data-pick]').forEach((btn) => { btn.onclick = () => pickImage((url) => { const s = btn.dataset.pick; draft[s].image = url; const img = $(`#m-${s}-img`); img.src = url; img.style.display = ''; }); });
  $$('[data-clear]').forEach((btn) => { btn.onclick = () => { draft[btn.dataset.clear].image = null; $(`#m-${btn.dataset.clear}-img`).style.display = 'none'; }; });
  $$('#m-swatches .swatch').forEach((sw) => { sw.onclick = () => { colorTouched = true; draft.color = +sw.dataset.color; setSwatch(); }; });
  const addGapRow = (val = '') => {
    const row = document.createElement('div');
    row.className = 'gap-row';
    row.innerHTML = `<input class="input gap-inp" placeholder="z. B. Paris" value="${esc(val)}"><button type="button" class="gap-del" title="Lücke entfernen">✕</button>`;
    row.querySelector('.gap-del').onclick = () => row.remove();
    $('#m-gaps').appendChild(row);
    return row;
  };
  initGaps.forEach((g) => addGapRow(g));
  $('#m-add-gap').onclick = () => { const r = addGapRow(); r.querySelector('.gap-inp').focus(); };
  $('#m-cloze').onchange = (e) => {
    const on = e.target.checked;
    $('#m-back-field').style.display = on ? 'none' : '';
    $('#m-cloze-box').style.display = on ? '' : 'none';
    $('#m-front-label').textContent = on ? 'Text' : 'Vorderseite – Frage';
    if (on && !$$('#m-gaps .gap-inp').length) addGapRow();
  };
}
export const unwrapCloze = (t) => String(t || '').replace(/\{\{(?:c\d+::)?([\s\S]+?)\}\}/g, '$1');
/* Ersetzt je Lückenwort das erste Vorkommen AUSSERHALB bereits vorhandener {{…}} durch {{wort}}. */
export function wrapClozes(text, gaps) {
  const missing = [];
  for (const g of gaps) {
    const parts = text.split(/(\{\{[\s\S]*?\}\})/);   // ungerade Indizes = bereits gesetzte Lücken
    let done = false;
    for (let i = 0; i < parts.length && !done; i += 2) {
      const idx = parts[i].indexOf(g);
      if (idx !== -1) { parts[i] = parts[i].slice(0, idx) + '{{' + g + '}}' + parts[i].slice(idx + g.length); done = true; }
    }
    text = parts.join('');
    if (!done) missing.push(g);
  }
  return { text, missing };
}
export function pickImage(cb) {
  const inp = document.createElement('input');
  inp.type = 'file'; inp.accept = 'image/*';
  inp.onchange = async () => { if (inp.files[0]) cb(await shrink(inp.files[0])); };
  inp.click();
}
export function shrink(file, max = 1000) {
  return new Promise((res) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const cv = document.createElement('canvas');
        cv.width = Math.round(img.width * scale); cv.height = Math.round(img.height * scale);
        cv.getContext('2d').drawImage(img, 0, 0, cv.width, cv.height);
        res(cv.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => res(reader.result);
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

